import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "../Scheduler/Scheduler.css";

const PRESETS = [
  { label: "Last 1 Week", days: 7 },
  { label: "Last 2 Weeks", days: 14 },
  { label: "Last 1 Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
  { label: "Last 6 Months", days: 180 },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDate = (d) => d.toISOString().split("T")[0];

const RcaScheduler = () => {
  const today = toInputDate(new Date());
  const [startDate, setStartDate] = useState(
    toInputDate(new Date(Date.now() - 7 * 86400000))
  );
  const [endDate, setEndDate] = useState(today);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/rca/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch RCA jobs:", err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const hasActive = jobs.some(
      (j) => j.status === "running" || j.status === "pending"
    );
    if (!hasActive) return;
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const handlePreset = (days) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    setStartDate(toInputDate(start));
    setEndDate(toInputDate(end));
  };

  const handleRunJob = async () => {
    setError("");
    setSuccess("");

    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date must be before end date.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("/rca/run", {
        start_date: startDate,
        end_date: endDate,
      });
      setSuccess(`RCA job ${res.data.job_id} started successfully.`);
      await fetchJobs();
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to start RCA job. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.post(`/rca/jobs/${jobId}/cancel`);
      await fetchJobs();
    } catch (err) {
      console.error("Failed to cancel job:", err);
    }
  };

  const isRunning = jobs.some(
    (j) => j.status === "running" || j.status === "pending"
  );

  return (
    <div>
      <Navbar />
      <div className="scheduler-container">
        <div className="scheduler-header">
          <h1>RCA Pre-computation Job</h1>
          <p>
            Pre-compute RCA analysis for <strong>Major</strong> and{" "}
            <strong>Critical</strong> tickets over a date range. Once computed,
            clicking RCA on those tickets loads instantly from the database.
          </p>
        </div>

        <div className="scheduler-card">
          <h2>Run New RCA Job</h2>
          <div className="date-form">
            <div className="date-field">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-field">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button
              className="run-btn"
              onClick={handleRunJob}
              disabled={loading || isRunning}
            >
              {loading
                ? "Starting..."
                : isRunning
                ? "Job Running..."
                : "Run RCA Job"}
            </button>
          </div>
          <div className="presets">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                className="preset-btn"
                onClick={() => handlePreset(p.days)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {error && <p className="error-msg">{error}</p>}
          {success && <p className="success-msg">{success}</p>}
        </div>

        <div className="scheduler-card">
          <h2>Job History</h2>
          {jobs.length === 0 ? (
            <p className="no-jobs">
              No RCA jobs yet. Select a date range and run your first job.
            </p>
          ) : (
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Date Range</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Tickets</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td style={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {job.id}
                      </td>
                      <td>
                        {job.start_date} → {job.end_date}
                      </td>
                      <td>
                        <span className={`status-badge status-${job.status}`}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <div className="progress-bar-container">
                          <div
                            className={`progress-bar-fill ${job.status}`}
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <span className="progress-text">{job.progress}%</span>
                      </td>
                      <td>
                        <span title="Processed">{job.processed_tickets}</span>
                        {job.skipped_tickets > 0 && (
                          <span
                            title="Already in DB"
                            style={{ color: "#a0aec0" }}
                          >
                            {" "}
                            +{job.skipped_tickets}s
                          </span>
                        )}
                        {job.error_tickets > 0 && (
                          <span title="Errors" style={{ color: "#e53e3e" }}>
                            {" "}
                            / {job.error_tickets}err
                          </span>
                        )}
                        <span style={{ color: "#718096" }}>
                          {" "}
                          / {job.total_tickets}
                        </span>
                      </td>
                      <td>{formatDate(job.started_at)}</td>
                      <td>{formatDate(job.completed_at)}</td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            className="log-toggle-btn"
                            onClick={() =>
                              setExpandedJob(
                                expandedJob === job.id ? null : job.id
                              )
                            }
                          >
                            {expandedJob === job.id ? "Hide" : "Logs"}
                          </button>
                          {(job.status === "running" ||
                            job.status === "pending") && (
                            <button
                              className="log-toggle-btn"
                              style={{ color: "#c53030", borderColor: "#feb2b2" }}
                              onClick={() => handleCancel(job.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedJob === job.id && (
                      <tr>
                        <td colSpan="8" style={{ padding: 0 }}>
                          <div className="log-panel">
                            {job.logs && job.logs.length > 0 ? (
                              job.logs.map((log, i) => <p key={i}>{log}</p>)
                            ) : (
                              <p>No logs yet.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default RcaScheduler;
